import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowRightLeft, FileUp, Loader2, Mail, Search, Star, Trash2, UserPlus, Users as UsersIcon } from 'lucide-react'
import { opcoApi } from '../../../services/api'
import { useToast } from '../../../context/ToastContext'
import { useOpCo } from '../../../context/OpCoContext'
import { workspaceNav, workspacePath } from '../../../config/opcoWorkspace'
import { AddUserModal } from '../../../pages/Users'
import MemberImportModal from './MemberImportModal'
import MoveWorkModal from './MoveWorkModal'
import WorkMoveRunsPanel from './WorkMoveRunsPanel'
import { hasWork } from './workMoveHelpers'
import {
  ConfirmDialog, Modal, PanelState, asList, cardCls, errorMessage, inputCls, primaryBtn, secondaryBtn,
} from './shared'

const ROLE_BADGE = {
  manager: 'bg-navy-50 text-navy-700 ring-navy-200 dark:bg-navy-800/50 dark:text-navy-200 dark:ring-navy-700',
  member: 'bg-gray-100 text-gray-700 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700',
}

function displayName(u) {
  return u?.username || u?.name || u?.display_name || u?.email || `User ${u?.user_id ?? u?.id}`
}

// Members of one OpCo. A person is either an Admin (the OpCo's manager, set on
// the Users page with the Admin role) or a plain member: there is no separate
// role to pick here. Super Admins can also change the primary flag. Both can
// assign people from a CSV, and invite new people into the OpCo they are working
// in (an invite always lands in the current workspace, so it is offered for that
// OpCo only).
export default function MembersPanel({ opco, canManageRoles, onShowAutoAssign }) {
  const toast = useToast()
  const { opco: workspace } = useOpCo()
  const inThisOpco = Boolean(workspace?.code) && workspace.code.toLowerCase() === String(opco.code || '').toLowerCase()
  // An invite always lands in the workspace the inviter is working in. Admins can
  // open any OpCo, so for them the button takes them there first instead of
  // disappearing in the All OpCos view; managers only ever work in their own.
  const canInvite = inThisOpco || Boolean(canManageRoles)
  const [showImport, setShowImport] = useState(false)
  const [showInvite, setShowInvite] = useState(false)
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [busyUser, setBusyUser] = useState(null)
  const [showAdd, setShowAdd] = useState(false)
  const [removing, setRemoving] = useState(null)
  const [moveFor, setMoveFor] = useState(null) // { person: {id, label}, plan } bringing someone's earlier work in
  const [runsKey, setRunsKey] = useState(0)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await opcoApi.listMembers(opco.id)
      setMembers(asList(res?.data, 'members'))
    } catch (err) {
      setError(errorMessage(err, 'Failed to load members'))
    } finally {
      setLoading(false)
    }
  }, [opco.id])

  useEffect(() => { load() }, [load])

  const updateMember = async (member, data, okText) => {
    setBusyUser(member.user_id)
    try {
      await opcoApi.updateMember(opco.id, member.user_id, data)
      toast.success('Updated', okText)
      await load()
    } catch (err) {
      toast.error('Update failed', errorMessage(err))
    } finally {
      setBusyUser(null)
    }
  }

  // After adding someone, offer to bring in what they made before they had an OpCo, but only
  // when there is something to show: the preview writes nothing, and a failure just skips the offer.
  const offerWorkMove = async (person) => {
    try {
      const res = await opcoApi.previewWorkMove(person.id, opco.id)
      if (hasWork(res?.data)) setMoveFor({ person, plan: res.data })
    } catch {
      // the Move work button on the member's row is still there
    }
  }

  const confirmRemove = async () => {
    const member = removing
    setBusyUser(member.user_id)
    try {
      await opcoApi.removeMember(opco.id, member.user_id)
      toast.success('Removed', `${displayName(member)} removed from ${opco.name}`)
      setRemoving(null)
      await load()
    } catch (err) {
      toast.error('Remove failed', errorMessage(err))
    } finally {
      setBusyUser(null)
    }
  }

  return (
    <section className={cardCls} aria-label="Members">
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 dark:border-gray-800 px-5 py-4">
        <div className="flex items-center gap-2">
          <UsersIcon className="h-4 w-4 text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Members</h2>
          {!loading && !error && (
            <span className="text-xs text-gray-500 dark:text-gray-400">{members.length}</span>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button type="button" onClick={() => setShowImport(true)} disabled={opco.is_active === false} className={secondaryBtn}>
            <FileUp className="h-4 w-4" /> Import members
          </button>
          {canInvite && (
            <button
              type="button"
              onClick={() => (inThisOpco
                ? setShowInvite(true)
                : workspaceNav.assign(workspacePath(opco.code, '/users-groups?tab=opcos')))}
              disabled={opco.is_active === false}
              title={inThisOpco ? undefined : `Opens ${opco.code} first, so the new person joins this OpCo`}
              className={secondaryBtn}
            >
              <Mail className="h-4 w-4" /> Invite people
            </button>
          )}
          <button type="button" onClick={() => setShowAdd(true)} disabled={opco.is_active === false} className={primaryBtn}>
            <UserPlus className="h-4 w-4" /> Add member
          </button>
        </div>
      </div>
      {onShowAutoAssign && (
        <p className="border-b border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/30 px-5 py-2 text-xs text-gray-600 dark:text-gray-400">
          Most people join automatically from their email address. Add people here only for exceptions.{' '}
          <button type="button" onClick={onShowAutoAssign} className="font-semibold text-navy-700 dark:text-navy-300 hover:underline">
            Set up Auto-assign
          </button>
        </p>
      )}
      <PanelState
        loading={loading}
        error={error}
        onRetry={load}
        empty={members.length === 0}
        emptyText="No members yet. Add people to give them access to this OpCo, or let Auto-assign place them."
      >
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {members.map((m) => {
            const busy = busyUser === m.user_id
            const role = m.role === 'manager' ? 'manager' : 'member'
            return (
              <li key={m.user_id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{displayName(m)}</span>
                    {m.is_primary && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-800">
                        <Star className="h-3 w-3" /> Primary
                      </span>
                    )}
                    {m.is_active === false && (
                      <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-400">Inactive</span>
                    )}
                    {m.added_via && (
                      <span
                        title={m.added_via === 'auto_sign_in' ? 'Added by an email rule when they first signed in' : 'Added by an email rule in a bulk apply'}
                        className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700 ring-1 ring-inset ring-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:ring-sky-800"
                      >
                        Added automatically
                      </span>
                    )}
                  </div>
                  {m.email && m.email !== displayName(m) && (
                    <p className="truncate text-xs text-gray-500 dark:text-gray-400">{m.email}</p>
                  )}
                </div>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${ROLE_BADGE[role]}`}>
                  {role === 'manager' ? 'Admin' : 'Member'}
                </span>
                {canManageRoles && m.is_active !== false && opco.is_active !== false && (
                  <button
                    type="button"
                    onClick={() => setMoveFor({ person: { id: m.user_id, label: displayName(m) }, plan: null })}
                    aria-label={`Move existing work of ${displayName(m)} into ${opco.name}`}
                    title="Bring the requests and templates this person made before they had an OpCo into this OpCo"
                    className="inline-flex h-8 items-center gap-1 rounded-lg px-2 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                  >
                    <ArrowRightLeft className="h-3.5 w-3.5" /> Move work
                  </button>
                )}
                {canManageRoles && !m.is_primary && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => updateMember(m, { is_primary: true }, `${opco.name} is now the primary OpCo for ${displayName(m)}`)}
                    className="h-8 rounded-lg px-2 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 disabled:opacity-50"
                    title="Make this the user's primary OpCo (their default workspace)"
                  >
                    Set primary
                  </button>
                )}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setRemoving(m)}
                  aria-label={`Remove ${displayName(m)}`}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 disabled:opacity-50"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </button>
              </li>
            )
          })}
        </ul>
      </PanelState>
      {canManageRoles && <WorkMoveRunsPanel opco={opco} reloadKey={runsKey} />}

      {showAdd && (
        <AddMemberModal
          opco={opco}
          existingIds={new Set(members.map((m) => String(m.user_id)))}
          onClose={() => setShowAdd(false)}
          onAdded={async (picked) => {
            setShowAdd(false)
            await load()
            if (canManageRoles && picked) await offerWorkMove({ id: picked.id, label: displayName(picked) })
          }}
        />
      )}
      {moveFor && (
        <MoveWorkModal
          opco={opco}
          person={moveFor.person}
          initialPlan={moveFor.plan}
          onClose={() => setMoveFor(null)}
          onDone={async () => { setMoveFor(null); setRunsKey((k) => k + 1) }}
        />
      )}
      {showImport && (
        <MemberImportModal
          opco={opco}
          onClose={() => setShowImport(false)}
          onImported={async ({ result }) => {
            toast.success('Users assigned', `${result?.added || 0} added, ${result?.role_changed || 0} role changes`)
            setShowImport(false)
            await load()
          }}
        />
      )}
      {canInvite && (
        <AddUserModal
          open={showInvite}
          onClose={() => setShowInvite(false)}
          onSuccess={load}
          canGrantAdmin={canManageRoles}
          opcoName={opco.name}
        />
      )}
      {removing && (
        <ConfirmDialog
          title="Remove member"
          message={`Remove ${displayName(removing)} from ${opco.name}? They lose access to this OpCo's data.`}
          confirmLabel="Remove"
          danger
          busy={busyUser === removing.user_id}
          onConfirm={confirmRemove}
          onCancel={() => setRemoving(null)}
        />
      )}
    </section>
  )
}

function AddMemberModal({ opco, existingIds, onClose, onAdded }) {
  const toast = useToast()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    opcoApi.candidates(opco.id)
      .then((res) => { if (!cancelled) setUsers(asList(res?.data, 'users')) })
      .catch((err) => { if (!cancelled) setError(errorMessage(err, 'Failed to load users')) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase()
    return users
      .filter((u) => u && u.id != null && !existingIds.has(String(u.id)) && u.is_active !== false)
      .filter((u) => !q || `${displayName(u)} ${u.email || ''}`.toLowerCase().includes(q))
      .slice(0, 50)
  }, [users, existingIds, query])

  const submit = async () => {
    if (!selected) return
    setSaving(true)
    try {
      await opcoApi.addMember(opco.id, selected.id, 'member')
      toast.success('Added', `${displayName(selected)} added to ${opco.name}`)
      await onAdded(selected)
    } catch (err) {
      toast.error('Add failed', errorMessage(err))
      setSaving(false)
    }
  }

  return (
    <Modal
      title={`Add member to ${opco.name}`}
      onClose={saving ? undefined : onClose}
      labelledBy="opco-add-member-title"
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={saving} className={secondaryBtn}>Cancel</button>
          <button type="button" onClick={submit} disabled={!selected || saving} className={primaryBtn}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Add
          </button>
        </>
      )}
    >
      <div className="space-y-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search users by name or email"
            aria-label="Search users"
            autoFocus
            className={`${inputCls} pl-9`}
          />
        </div>
        <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-100 dark:border-gray-800">
          <PanelState
            loading={loading}
            error={error}
            empty={candidates.length === 0}
            emptyText={query ? 'No matching users.' : 'Everyone is already a member.'}
          >
            <ul role="listbox" aria-label="Users">
              {candidates.map((u) => {
                const isSel = selected?.id === u.id
                return (
                  <li key={u.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSel}
                      onClick={() => setSelected(u)}
                      className={`flex w-full flex-col items-start px-3 py-2 text-left text-sm ${
                        isSel ? 'bg-navy-50 dark:bg-navy-800/40' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <span className="font-medium text-gray-900 dark:text-gray-100">{displayName(u)}</span>
                      {u.email && <span className="text-xs text-gray-500 dark:text-gray-400">{u.email}</span>}
                    </button>
                  </li>
                )
              })}
            </ul>
          </PanelState>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Only people already in Xtract are listed. Use Invite people to add someone new. To make someone an Admin, give them the Admin role on the Users page.
        </p>
      </div>
    </Modal>
  )
}
